import { TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { IncrementalHash } from '../../core/spatialHash';
import type { RenderTarget, TechniqueContext } from '../types';
import type { MossValues } from './params';

/**
 * Moss and coral growth along the letterforms.
 *
 * Cells bud from existing cells rather than being scattered: each new cell is placed a
 * short step from a randomly chosen live one, biased outward along the field gradient
 * and rejected if it lands too close to a neighbour. Growth therefore accretes into a
 * crust that thickens over time, instead of the uniform fuzz a scattering would give.
 *
 * The frontier is the set of cells that can still bud. A cell that fails repeatedly is
 * retired, which keeps growth on the surface rather than churning inside the mass.
 */
export interface MossCell {
  x: number;
  y: number;
  size: number;
  thickness: number;
  generation: number;
  tint: number;
}

export interface MossState {
  cells: MossCell[];
  frontier: number[];
  hash: IncrementalHash;
  palette: ResolvedPalette;
  generation: number;
  targetCells: number;
  maxThicknessSeen: number;
  done: boolean;
}

function values(ctx: TechniqueContext): MossValues {
  return ctx.values as unknown as MossValues;
}

export function init(ctx: TechniqueContext): MossState {
  const v = values(ctx);
  const minSize = Math.min(v.size[0], v.size[1]);

  const state: MossState = {
    cells: [],
    frontier: [],
    hash: new IncrementalHash(ctx.width, ctx.height, Math.max(2, minSize * v.density * 2)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    generation: 0,
    targetCells: v.cells,
    maxThicknessSeen: 1,
    done: false,
  };

  // Seed along the contour on the chosen side.
  const seeds = ctx.field.sampleOutline(Math.min(6000, Math.max(80, Math.round(v.cells / 12))));
  const sign = v.side === 'inside' ? -1 : 1;
  for (const s of seeds) {
    const x = s.x + s.nx * sign * 0.6;
    const y = s.y + s.ny * sign * 0.6;
    const id = state.cells.length;
    state.cells.push({
      x,
      y,
      size: ctx.rng.range(v.size[0], v.size[1]),
      thickness: 0,
      generation: 0,
      tint: ctx.rng.next(),
    });
    state.hash.insert(x, y, id);
    state.frontier.push(id);
  }

  return state;
}

export function step(state: MossState, _dt: number, ctx: TechniqueContext): MossState {
  if (state.done) return state;
  const v = values(ctx);
  const sign = v.side === 'inside' ? -1 : 1;
  const budget = Math.max(200, Math.round(3000 * clamp01(ctx.quality)));

  for (let n = 0; n < budget; n++) {
    if (state.cells.length >= state.targetCells || state.frontier.length === 0) {
      state.done = true;
      break;
    }

    const pick = ctx.rng.int(0, state.frontier.length - 1);
    const parentId = state.frontier[pick];
    const parent = state.cells[parentId];

    const size = ctx.rng.range(v.size[0], v.size[1]);
    const spacing = size * v.density;

    let placed = false;
    for (let attempt = 0; attempt < 8; attempt++) {
      // Outward along the field gradient, plus roughness and the direction bias.
      const [gx, gy] = ctx.field.gradient(parent.x, parent.y);
      let angle: number;
      if (gx === 0 && gy === 0) {
        angle = ctx.rng.next() * TAU;
      } else {
        angle = Math.atan2(gy * sign, gx * sign) + ctx.rng.signed(v.roughness * Math.PI);
      }
      if (v.directionBias !== 0) {
        const target = v.directionBias > 0 ? Math.PI / 2 : -Math.PI / 2;
        const delta = ((target - angle + Math.PI * 3) % TAU) - Math.PI;
        angle += delta * Math.abs(v.directionBias) * 0.4;
      }

      const step = spacing * v.growthRate * (0.7 + ctx.rng.next() * 0.6);
      const x = parent.x + Math.cos(angle) * step;
      const y = parent.y + Math.sin(angle) * step;
      if (x < 0 || y < 0 || x >= ctx.width || y >= ctx.height) continue;

      const signed = ctx.field.distance(x, y);
      const thickness = sign > 0 ? signed : -signed;
      // Stay on the chosen side and within the thickness limit.
      if (thickness < -1 || thickness > v.maxThickness) continue;

      let blocked = false;
      state.hash.query(x, y, spacing, (id) => {
        const other = state.cells[id];
        const dx = other.x - x;
        const dy = other.y - y;
        if (dx * dx + dy * dy < spacing * spacing) {
          blocked = true;
          return false;
        }
        return true;
      });
      if (blocked) continue;

      const id = state.cells.length;
      state.cells.push({
        x,
        y,
        size,
        thickness: Math.max(0, thickness),
        generation: state.generation,
        tint: ctx.rng.next(),
      });
      state.hash.insert(x, y, id);
      state.frontier.push(id);
      if (thickness > state.maxThicknessSeen) state.maxThicknessSeen = thickness;
      placed = true;
      break;
    }

    if (!placed) {
      // Retire a cell that can no longer bud, so the frontier stays on the surface.
      state.frontier[pick] = state.frontier[state.frontier.length - 1];
      state.frontier.pop();
    }
    state.generation++;
  }

  return state;
}

export function isComplete(state: MossState): boolean {
  return state.done;
}

export function draw(state: MossState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const buckets = new Map<string, Path2D>();

  for (const cell of state.cells) {
    let t: number;
    switch (v.colorMode) {
      case 'thickness':
        t = clamp01(cell.thickness / state.maxThicknessSeen);
        break;
      case 'generation':
        t = clamp01(cell.generation / Math.max(1, state.generation));
        break;
      case 'random':
        t = cell.tint;
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    let path = buckets.get(color);
    if (!path) {
      path = new Path2D();
      buckets.set(color, path);
    }
    path.moveTo(cell.x + cell.size, cell.y);
    path.arc(cell.x, cell.y, cell.size, 0, TAU);
  }

  for (const [color, path] of buckets) {
    c.fillStyle = color;
    c.fill(path);
  }
}

export function elementCount(state: MossState): number {
  return state.cells.length;
}
